import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { closeModal } from "../../../../redux/actions/action-creator";

const Print = ({
  data,
  sourceRef,
  companyName,
  companyInfo,
  defaultTitleToPrint,
  isAlter,
  isView,
}) => {
  const dispatch = useDispatch();
  const [showPreview, setShowPreview] = useState(false);
  const [activeAction, setActiveAction] = useState("");
  const previewContentRef = useRef(null);
  const sourceContentRef = sourceRef || previewContentRef;

  const normalizeUnitText = (value) => {
    const text = String(value || "").trim();
    if (!text) return "";
    // Drop pure numeric/id-like values that are not valid unit labels.
    if (/^\d+(\.\d+)?$/.test(text)) return "";
    return text;
  };

  const formatAltUnitDisplay = (row) => {
    const combination = String(row?.combination_of_units || "")
      .replace(/[()]/g, "")
      .trim();

    if (combination && /[a-zA-Z]/.test(combination)) {
      return combination.replace(/\+/g, " + ");
    }

    const altUnitName =
      normalizeUnitText(row?.item_alternate_unit) ||
      normalizeUnitText(row?.alt_unit_name) ||
      normalizeUnitText(row?.where_alternate_unit_name) ||
      normalizeUnitText(row?.total_no_box_unit) ||
      "";

    const altQtyRaw = String(row?.total_nos_box || "")
      .replace(/[()]/g, "")
      .trim();

    if (altQtyRaw && /[a-zA-Z]/.test(altQtyRaw)) {
      return altQtyRaw;
    }

    if (altQtyRaw && altUnitName) {
      return `${altQtyRaw} ${altUnitName}`;
    }

    return altUnitName;
  };

  const lineItems = useMemo(() => {
    const source = Array.isArray(data?.data_item) ? data.data_item : [];
    return source
      .filter((row) => (row?.item_name || "").trim() !== "")
      .map((row, index) => ({
        id: row?.id || String(index + 1),
        sl: index + 1,
        item_name: row?.item_name || "",
        qty: row?.item_qty || "",
        alt_unit: formatAltUnitDisplay(row),
        rate: row?.item_rate || "",
        per: row?.per_unit || "",
        amount: row?.item_amt || "0",
      }));
  }, [data?.data_item]);

  const additionalLedgerRows = useMemo(() => {
    const source = Array.isArray(data?.sale_ledger_data)
      ? data.sale_ledger_data
      : [];
    return source
      .filter((row) => (row?.ledger_account || "").trim() !== "")
      .map((row, index) => ({
        id: row?.id || `AL_${index + 1}`,
        ledger_account: row?.ledger_account || "",
        ledger_amount:
          Number(String(row?.ledger_amount || "0").replace(/,/g, "")) || 0,
      }));
  }, [data?.sale_ledger_data]);

  const paymentAllocations = useMemo(() => {
    const source = Array.isArray(data?.payment_allocation)
      ? data.payment_allocation
      : [];
    return source
      .filter((row) => (row?.ledger_name || "").trim() !== "")
      .map((row, index) => ({
        id: row?.id || `PA_${index + 1}`,
        ledger_name: row?.ledger_name || "",
        amount:
          Number(String(row?.payment_ledger_amount || "0").replace(/,/g, "")) ||
          0,
      }));
  }, [data?.payment_allocation]);

  const totalAmount = useMemo(() => {
    return lineItems.reduce((acc, item) => {
      const normalized = String(item.amount || "0").replace(/,/g, "");
      return acc + (Number(normalized) || 0);
    }, 0);
  }, [lineItems]);

  const totalQuantity = useMemo(() => {
    return lineItems.reduce((acc, item) => {
      const qtyText = String(item?.qty || "");
      const match = qtyText.match(/-?\d+(?:\.\d+)?/);
      const qtyValue = match ? Number(match[0]) : 0;
      return acc + (Number.isFinite(qtyValue) ? qtyValue : 0);
    }, 0);
  }, [lineItems]);

  const formatQuantity = (value) => {
    const num = Number(value) || 0;
    const hasFraction = Math.abs(num % 1) > 0;
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: 2,
    });
  };

  const additionalLedgerTotal = useMemo(() => {
    return additionalLedgerRows.reduce(
      (acc, row) => acc + (Number(row?.ledger_amount) || 0),
      0,
    );
  }, [additionalLedgerRows]);

  const paymentAllocationTotal = useMemo(() => {
    return paymentAllocations.reduce(
      (acc, row) => acc + (Number(row?.amount) || 0),
      0,
    );
  }, [paymentAllocations]);

  const grossVoucherAmount = useMemo(() => {
    return (Number(totalAmount) || 0) + (Number(additionalLedgerTotal) || 0);
  }, [totalAmount, additionalLedgerTotal]);

  const netPayableAmount = useMemo(() => {
    return grossVoucherAmount - (Number(paymentAllocationTotal) || 0);
  }, [grossVoucherAmount, paymentAllocationTotal]);

  const formatInr = (value) => {
    const num = Number(value) || 0;
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const numberToWordsIndian = (value) => {
    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const twoDigits = (num) => {
      if (num < 20) return ones[num];
      const t = Math.floor(num / 10);
      const o = num % 10;
      return `${tens[t]}${o ? ` ${ones[o]}` : ""}`.trim();
    };

    const threeDigits = (num) => {
      const h = Math.floor(num / 100);
      const rest = num % 100;
      const hText = h ? `${ones[h]} Hundred` : "";
      const rText = rest ? twoDigits(rest) : "";
      return `${hText}${hText && rText ? " " : ""}${rText}`.trim();
    };

    const n = Math.floor(Number(value) || 0);
    if (!n) return "Zero";

    const crore = Math.floor(n / 10000000);
    const lakh = Math.floor((n % 10000000) / 100000);
    const thousand = Math.floor((n % 100000) / 1000);
    const hundred = n % 1000;

    const parts = [];
    if (crore) parts.push(`${threeDigits(crore)} Crore`);
    if (lakh) parts.push(`${threeDigits(lakh)} Lakh`);
    if (thousand) parts.push(`${threeDigits(thousand)} Thousand`);
    if (hundred) parts.push(threeDigits(hundred));
    return parts.join(" ").trim();
  };

  const wordsForNetPayable = useMemo(() => {
    return `${numberToWordsIndian(netPayableAmount)} Only`;
  }, [netPayableAmount]);

  const printableTitle =
    String(defaultTitleToPrint || data?.default_title_to_print || "").trim() ||
    "TAX INVOICE";

  const companyAddressLine = useMemo(() => {
    const parts = [
      companyInfo?.address,
      companyInfo?.city,
      companyInfo?.state,
      companyInfo?.pincode,
    ]
      .map((part) => String(part || "").trim())
      .filter(Boolean);

    if (parts.length > 0) return parts.join(", ");
    return "Address not available";
  }, [companyInfo]);

  const companyContactLine = useMemo(() => {
    const mobile = String(companyInfo?.mobile || "").trim();
    const email = String(companyInfo?.email || "").trim();
    const gst = String(
      companyInfo?.gst_no ||
        companyInfo?.gstin ||
        companyInfo?.gst_number ||
        data?.gst_no ||
        data?.gstin ||
        "",
    ).trim();

    const segments = [];
    if (mobile) segments.push(`Mobile: ${mobile}`);
    if (email) segments.push(`Email: ${email}`);
    if (gst) segments.push(`GST: ${gst}`);
    return segments.length > 0
      ? segments.join(" | ")
      : "Contact details not available";
  }, [companyInfo, data]);

  const itemTableRows = useMemo(() => {
    const itemRows = lineItems.map((row) => ({
      id: row.id,
      sl: row.sl,
      isLedgerRow: false,
      item_name: row.item_name,
      qty: row.qty,
      alt_unit: row.alt_unit,
      rate: row.rate,
      per: row.per,
      amount: row.amount,
    }));

    const ledgerRows = additionalLedgerRows.map((row) => ({
      id: `LEDGER_ROW_${row.id}`,
      sl: "",
      isLedgerRow: true,
      item_name: row.ledger_account,
      qty: "",
      alt_unit: "",
      rate: "",
      per: "",
      amount: row.ledger_amount,
    }));

    return [...itemRows, ...ledgerRows];
  }, [lineItems, additionalLedgerRows]);

  const previewRows = useMemo(() => {
    const minRows = 12;
    if (itemTableRows.length >= minRows) return itemTableRows;
    const blanks = Array.from({ length: minRows - itemTableRows.length }).map(
      (_, idx) => ({
        id: `blank_${idx + 1}`,
        sl: "",
        isLedgerRow: false,
        item_name: "",
        qty: "",
        alt_unit: "",
        rate: "",
        per: "",
        amount: "",
      }),
    );
    return [...itemTableRows, ...blanks];
  }, [itemTableRows]);

  const closePrintModal = () => {
    dispatch(closeModal("print"));
  };

  const openPreview = () => {
    setActiveAction("preview");
    setShowPreview(true);
  };

  const sendPreviewToBrowserPrint = () => {
    const previewNode = sourceContentRef.current;
    if (!previewNode) return;

    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;

    const printStyles = `
      <style>
        @page { size: A4 portrait; margin: 8mm; }
        html, body { margin: 0; padding: 0; background: #fff; }
        body { font-family: Arial, sans-serif; }
      </style>
    `;

    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>Voucher Print Preview</title>
          ${printStyles}
        </head>
        <body>
          ${previewNode.outerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 120);
  };

  const triggerBrowserPrint = () => {
    setActiveAction("print");

    if (!showPreview) {
      setShowPreview(true);
      setTimeout(() => {
        sendPreviewToBrowserPrint();
      }, 180);
      return;
    }

    sendPreviewToBrowserPrint();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === "function") {
          e.stopImmediatePropagation();
        }
        if (showPreview) {
          setShowPreview(false);
          setActiveAction("");
          return;
        }
        closePrintModal();
      }

      if (e.key === "i" || e.key === "I") {
        e.preventDefault();
        openPreview();
      }

      if (e.key === "c" || e.key === "C") {
        e.preventDefault();
        setActiveAction("configure");
      }

      if (e.key === "p" || e.key === "P") {
        e.preventDefault();
        triggerBrowserPrint();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showPreview]);

  const voucherTitle = `${data?.voucher_type_name || "Voucher"} ${
    data?.serial_no ? `No. ${data.serial_no}` : ""
  }`.trim();
  const showOnlyPrintButton = showPreview && (isAlter || isView);

  const shipToMailingName =
    data?.party_ship_to_mailing_name ||
    data?.mailing_name_ship_to ||
    data?.party_bill_to_mailing_name ||
    data?.mailing_name_bill_to ||
    "";
  const shipToAddress =
    data?.ship_to_city || data?.city_ship_to || data?.bill_to_city || "";
  const shipToContact =
    data?.ship_to_contact ||
    data?.contact_ship_to ||
    data?.bill_to_contact ||
    "";

  const billToMailingName =
    data?.party_bill_to_mailing_name || data?.mailing_name_bill_to || "";
  const billToCity = data?.bill_to_city || data?.city_bill_to || "";
  const billToContact = data?.bill_to_contact || data?.contact_bill_to || "";

  const shipToCity =
    data?.ship_to_city ||
    data?.city_ship_to ||
    data?.bill_to_city ||
    data?.city_bill_to ||
    "";

  return (
    <div className="my-card" style={{ zIndex: 1000 }}>
      <div className="cross_button" onClick={closePrintModal}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          className="bi bi-x"
          viewBox="0 0 16 16"
        >
          <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
        </svg>
      </div>

      <div
        className="container border border-dark p-3"
        style={{ width: "46%", background: "#fff", minHeight: "36vh" }}
      >
        <div className="row border-bottom mb-2">
          <div className="col d-flex justify-content-between">
            <b>Print</b>
            <b className="text-truncate" style={{ maxWidth: "65%" }}>
              {voucherTitle}
            </b>
          </div>
        </div>

        <div className="row">
          <div className="col-5">Title</div>
          <div className="col-1">:</div>
          <div className="col-6">{data?.voucher_type_name || "Voucher"}</div>
        </div>
        <div className="row">
          <div className="col-5">Printer</div>
          <div className="col-1">:</div>
          <div className="col-6">Default Printer</div>
        </div>
        <div className="row">
          <div className="col-5">Paper Size</div>
          <div className="col-1">:</div>
          <div className="col-6">A4 (210 mm x 297 mm)</div>
        </div>
        <div className="row">
          <div className="col-5">Print Area</div>
          <div className="col-1">:</div>
          <div className="col-6">Full Page A4</div>
        </div>
        <div className="row">
          <div className="col-5">Number of Copies</div>
          <div className="col-1">:</div>
          <div className="col-6">1</div>
        </div>

        <div className="row mt-3">
          <div className="col d-flex justify-content-center gap-3">
            <button
              className="button_padding"
              onClick={() => setActiveAction("configure")}
              disabled={showOnlyPrintButton}
              style={{
                minWidth: 100,
                visibility: showOnlyPrintButton ? "hidden" : "visible",
                border:
                  activeAction === "configure"
                    ? "2px solid #f0ad4e"
                    : "1px solid #000",
              }}
            >
              C: Configure
            </button>
            <button
              className="button_padding"
              onClick={openPreview}
              disabled={showOnlyPrintButton}
              style={{
                minWidth: 100,
                visibility: showOnlyPrintButton ? "hidden" : "visible",
                border:
                  activeAction === "preview"
                    ? "2px solid #f0ad4e"
                    : "1px solid #000",
              }}
            >
              I: Preview
            </button>
            <button
              className="button_padding"
              onClick={triggerBrowserPrint}
              style={{
                minWidth: 100,
                border:
                  activeAction === "print"
                    ? "2px solid #f0ad4e"
                    : "1px solid #000",
              }}
            >
              P: Print
            </button>
          </div>
        </div>
      </div>

      {showPreview && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 1100,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            overflow: "auto",
            padding: "18px 0",
          }}
        >
          <div style={{ position: "relative" }}>
            <button
              onClick={() => {
                setShowPreview(false);
                setActiveAction("");
              }}
              style={{
                position: "absolute",
                right: 8,
                top: 8,
                border: "1px solid #000",
                background: "#fff",
                zIndex: 2,
                width: 24,
                height: 24,
                lineHeight: "20px",
                textAlign: "center",
              }}
            >
              x
            </button>

            <div
              ref={previewContentRef}
              style={{
                width: "794px",
                minHeight: "1123px",
                background: "#fff",
                border: "1px solid #000",
                boxShadow: "none",
                padding: "18px",
                fontSize: "12px",
                color: "#111",
              }}
            >
              <div
                style={{
                  textAlign: "center",
                  fontWeight: 700,
                  fontSize: "18px",
                }}
              >
                {companyName || data?.company_name || "Company Name"}
              </div>
              <div style={{ textAlign: "center", marginTop: 2 }}>
                {companyAddressLine}
              </div>
              <div style={{ textAlign: "center", marginTop: 2 }}>
                {companyContactLine}
              </div>
              <div
                style={{
                  textAlign: "center",
                  fontWeight: 900,
                  fontSize: "14px",
                  marginTop: 8,
                  borderTop: "1px solid #000",
                  borderBottom: "1px solid #000",
                  padding: "4px 0",
                }}
              >
                {printableTitle}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  marginTop: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      border: "1px solid #000",
                      padding: 8,
                      flex: 1,
                      textAlign: "left",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>
                      Consignee (Ship to)
                    </div>
                    <div style={{ paddingLeft: 12 }}>{shipToMailingName}</div>
                    <div style={{ paddingLeft: 12 }}>{shipToCity}</div>
                    <div style={{ paddingLeft: 12 }}>{shipToContact}</div>
                  </div>

                  <div
                    style={{
                      border: "1px solid #000",
                      padding: 8,
                      flex: 1,
                      textAlign: "left",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>
                      Buyer (Bill to)
                    </div>
                    <div style={{ paddingLeft: 12 }}>{billToMailingName}</div>
                    <div style={{ paddingLeft: 12 }}>{billToCity}</div>
                    <div style={{ paddingLeft: 12 }}>{billToContact}</div>
                  </div>
                </div>

                <div
                  style={{
                    border: "1px solid #000",
                    padding: 8,
                    minHeight: 200,
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "44% 56%",
                      rowGap: 4,
                      textAlign: "left",
                    }}
                  >
                    <div>Invoice No.</div>
                    <div>: {data?.serial_no || "-"}</div>

                    <div>Dated</div>
                    <div>: {data?.create_date || "-"}</div>

                    <div>Delivery Note</div>
                    <div>: {data?.delivery_note_no || "-"}</div>

                    <div>Mode/Terms of Payment</div>
                    <div>: {data?.mode_term_payment || "-"}</div>

                    <div>Dispatched Through</div>
                    <div>: {data?.dispatch_through || "-"}</div>

                    <div>Destination</div>
                    <div>: {data?.destination || "-"}</div>

                    <div>Dispatch Doc No.</div>
                    <div>: {data?.dispatch_doc_no || "-"}</div>

                    <div>Driver/Agent</div>
                    <div>: {data?.carrier_name_agent || "-"}</div>

                    <div>Mobile No.</div>
                    <div>
                      :{" "}
                      {data?.carrier_mobile_no ||
                        data?.carrier_mobile_number ||
                        data?.agent_mobile_no ||
                        "-"}
                    </div>

                    <div>Vehical No.</div>
                    <div>
                      : {data?.vehicle_number || data?.vehical_number || "-"}
                    </div>
                  </div>
                </div>
              </div>

              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginTop: 10,
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "4px",
                        width: "6%",
                      }}
                    >
                      Sl
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "4px",
                        width: "38%",
                      }}
                    >
                      Description of Goods
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "4px",
                        width: "12%",
                      }}
                    >
                      Quantity
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "4px",
                        width: "12%",
                      }}
                    >
                      Alt. Unit
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "4px",
                        width: "12%",
                      }}
                    >
                      Rate
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "4px",
                        width: "8%",
                      }}
                    >
                      Per
                    </th>
                    <th
                      style={{
                        border: "1px solid #000",
                        padding: "4px",
                        width: "14%",
                      }}
                    >
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {itemTableRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        style={{
                          border: "1px solid #000",
                          padding: 8,
                          textAlign: "center",
                        }}
                      >
                        No item rows available in voucher
                      </td>
                    </tr>
                  ) : (
                    previewRows.map((item) => (
                      <tr key={item.id}>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "4px",
                            textAlign: "right",
                            height: "26px",
                          }}
                        >
                          {item.isLedgerRow ? "" : item.sl}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "4px",
                            textAlign: item.isLedgerRow ? "left" : "left",
                          }}
                        >
                          {item.item_name}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "4px",
                            textAlign: "right",
                          }}
                        >
                          {item.qty}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "4px",
                            textAlign: "right",
                          }}
                        >
                          {item.alt_unit ? `(${item.alt_unit})` : ""}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "4px",
                            textAlign: "right",
                          }}
                        >
                          {item.rate
                            ? formatInr(String(item.rate).replace(/,/g, ""))
                            : ""}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "4px",
                            textAlign: "center",
                          }}
                        >
                          {item.per}
                        </td>
                        <td
                          style={{
                            border: "1px solid #000",
                            padding: "4px",
                            textAlign: "right",
                          }}
                        >
                          {item.amount
                            ? formatInr(String(item.amount).replace(/,/g, ""))
                            : ""}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "right",
                        fontWeight: 700,
                      }}
                    ></td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "right",
                        fontWeight: 700,
                      }}
                    >
                      Total
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "right",
                        fontWeight: 700,
                      }}
                    >
                      {formatQuantity(totalQuantity)}
                    </td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                      }}
                    ></td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                      }}
                    ></td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                      }}
                    ></td>
                    <td
                      style={{
                        border: "1px solid #000",
                        padding: "6px",
                        textAlign: "right",
                        fontWeight: 700,
                      }}
                    >
                      {formatInr(grossVoucherAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>

              <div
                style={{
                  marginTop: 8,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    border: "1px solid #000",
                    padding: "6px",
                    minHeight: 96,
                    textAlign: "left",
                  }}
                >
                  <div style={{ marginBottom: 4 }}>
                    <b>Narration</b> {data?.narration || "-"}
                  </div>
                </div>

                <div style={{ border: "1px solid #000" }}>
                  <div style={{ padding: "6px 8px" }}>
                    <div
                      style={{
                        fontWeight: 700,
                        marginBottom: 4,
                        textAlign: "left",
                      }}
                    >
                      Payment Allocation
                    </div>
                    {paymentAllocations.length > 0 ? (
                      paymentAllocations.map((row) => (
                        <div
                          key={row.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span style={{ paddingLeft: "18px" }}>
                            {row.ledger_name}
                          </span>
                          <span>{formatInr(row.amount)}</span>
                        </div>
                      ))
                    ) : (
                      <div style={{ color: "#333" }}>-</div>
                    )}
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: 8,
                  border: "1px solid #000",
                  padding: "6px 8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: 700,
                  }}
                >
                  <span>Net Payable ({wordsForNetPayable})</span>
                  <span>{formatInr(netPayableAmount)}</span>
                </div>
              </div>

              <div
                style={{
                  marginTop: 8,
                  border: "1px solid #000",
                  padding: "6px",
                  minHeight: 72,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    minHeight: 56,
                  }}
                >
                  <span style={{ alignSelf: "flex-start" }}>
                    Receiver Signature
                  </span>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                      minHeight: 56,
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>
                      For {companyName || data?.company_name || "Company Name"}
                    </span>
                    <span>Authorised Signatory</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Print;
